import React, { useRef, useState } from 'react';
import useApiContext from 'hooks/useApiContext';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  HierarchyImportError,
  iHierarchyImportError,
  iHierarchyImportResult,
} from 'services/Category/Category.repository';
import { eRoles } from '../../interfaces/Roles';

interface Props {
  onImported: () => void;
}

function HierarchyImport({ onImported }: Props): JSX.Element | null {
  const { userRepository, categoryRepository } = useApiContext();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<iHierarchyImportResult | null>(null);
  const [errors, setErrors] = useState<iHierarchyImportError[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  const user = userRepository.getUser();
  if (user?.role !== eRoles.SUPER_ADMIN) return null;

  const reset = (): void => {
    setResult(null);
    setErrors([]);
    setErrorMessage('');
  };

  const handleTemplate = async (): Promise<void> => {
    try {
      const blob = await categoryRepository.downloadHierarchyTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'universes_categories_subcategories_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleFile = async (
    e: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    reset();
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      setErrorMessage(t('categories:import_select_file'));
      return;
    }
    setLoading(true);
    setProgress(0);
    try {
      const res = await categoryRepository.importHierarchy(file, setProgress);
      setResult(res);
      toast.success(t('categories:import_done'));
      onImported();
    } catch (err: any) {
      setErrorMessage(err.message);
      if (err instanceof HierarchyImportError) setErrors(err.errors);
    } finally {
      setLoading(false);
    }
  };

  const btn: React.CSSProperties = {
    padding: '8px 14px',
    border: '1px solid #ccc',
    borderRadius: 4,
    background: 'white',
    cursor: 'pointer',
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          onChange={handleFile}
        />
        <button type="button" style={btn} onClick={handleTemplate}>
          {t('categories:import_template')}
        </button>
        <button
          type="button"
          style={btn}
          disabled={loading}
          onClick={() => inputRef.current?.click()}
        >
          {loading
            ? `${t('categories:import_loading')} ${progress}%`
            : t('categories:import_excel')}
        </button>
      </div>

      {loading && (
        <div
          style={{
            height: 6,
            marginTop: 8,
            background: '#eee',
            borderRadius: 3,
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progress}%`,
              background: '#1976d2',
              borderRadius: 3,
              transition: 'width .2s',
            }}
          />
        </div>
      )}

      {result && (
        <div
          style={{
            marginTop: 10,
            padding: 12,
            border: '1px solid #b7e1b7',
            background: '#f0fbf0',
            borderRadius: 4,
          }}
        >
          <strong>{t('categories:import_done')}</strong>
          <ul style={{ margin: '6px 0 0 18px' }}>
            {(
              [
                ['universes', 'import_universes'],
                ['categories', 'import_categories'],
                ['subcategories', 'import_subcategories'],
              ] as const
            ).map(([k, label]) => (
              <li key={k}>
                {t(`categories:${label}`)}: {result.stats[k].created}{' '}
                {t('categories:import_created')} / {result.stats[k].existing}{' '}
                {t('categories:import_existing')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {errorMessage && (
        <div
          style={{
            marginTop: 10,
            padding: 12,
            border: '1px solid #f0b4b4',
            background: '#fdf0f0',
            borderRadius: 4,
          }}
        >
          <strong>{errorMessage}</strong>
          {errors.length > 0 && (
            <ul
              style={{
                margin: '6px 0 0 18px',
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              {errors.map((er, i) => (
                <li key={i}>
                  {er.row > 0
                    ? `${t('categories:import_row')} ${er.row}: `
                    : ''}
                  {er.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default HierarchyImport;
